/**
 * Shared helpers for the news/blog feature.
 *
 * The news list (components/NewsBlog.tsx) and the full story page
 * (pages/NewsStoryPage.tsx) previously each carried their own copy of these
 * lookups and translation-resolution logic. They live here so a post only has
 * to be registered once.
 */

/** Badge colors per news category. */
export const CATEGORY_COLORS: Record<string, string> = {
  Political: 'bg-haa-blue text-white',
  Immigration: 'bg-haa-skyblue text-white',
  Business: 'bg-emerald-600 text-white',
  'Note Worthy': 'bg-haa-midblue text-white',
  'Arts & Entertainment': 'bg-haa-navy text-white',
  Events: 'bg-blue-400 text-white',
  Sports: 'bg-orange-500 text-white',
}

export function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? 'bg-haa-blue text-white'
}

/** Translation keys for each news category label. */
export const CATEGORY_KEY_MAP: Record<string, string> = {
  Political: 'news.catPolitical',
  Immigration: 'news.catImmigration',
  Business: 'news.catBusiness',
  'Note Worthy': 'news.catNoteWorthy',
  'Arts & Entertainment': 'news.catArtMedia',
  Events: 'news.catEvents',
  Sports: 'news.catSports',
}

/**
 * Post id → translation key number. A post must be listed here for its
 * Spanish translation to be picked up; unlisted posts fall back to the
 * English text stored on the post itself.
 */
export const POST_ID_MAP: Record<string, string> = {
  'post-45': '45',
  'post-44': '44',
  'post-43': '43',
  'post-42': '42',
  'post-41': '41',
  'post-40': '40',
  'post-1': '1',
  'post-2': '2',
  'post-5': '5',
  'post-6': '6',
  'post-7': '7',
  'post-8': '8',
  'post-9': '9',
  'post-10': '10',
  'post-11': '11',
  'post-12': '12',
  'post-13': '13',
  'post-14': '14',
  'post-15': '15',
  'post-16': '16',
  'post-17': '17',
  'post-18': '18',
  'post-19': '19',
  'post-20': '20',
  'post-21': '21',
  'post-22': '22',
  'post-23': '23',
  'post-24': '24',
  'post-25': '25',
  'post-26': '26',
  'post-27': '27',
  'post-28': '28',
  'post-29': '29',
  'post-30': '30',
  'post-31': '31',
  'post-32': '32',
  'post-33': '33',
  'post-34': '34',
  'post-35': '35',
  'post-36': '36',
  'post-37': '37',
}

/** Returns the translation key number for a post, or '' when untranslated. */
export function getPostNum(postId: string): string {
  return POST_ID_MAP[postId] ?? ''
}

type Translate = (key: string) => string

/**
 * Resolves one translated field of a post ('title', 'excerpt', 'date',
 * 'readTime', 'fullStory'), falling back to the post's own value whenever
 * a translation is missing.
 */
export function resolveTranslatedField(
  t: Translate,
  postNum: string,
  field: string,
  fallback?: string,
): string | undefined {
  if (!postNum) return fallback
  const key = `news.post${postNum}.${field}`
  const translation = t(key)
  if (!translation || translation === key) return fallback
  return translation
}

/** Resolves a category's translated label, falling back to the raw name. */
export function translateCategory(t: Translate, category: string): string {
  const key = CATEGORY_KEY_MAP[category]
  return key ? t(key) : category
}
